// Prueba de un examen por el personal — openspec/changes/section-quiz-expert-test.
// Tipos del contrato con la API y utilidades puras (formateo y armado de respuestas),
// separadas de los componentes para poder probarlas sin DOM.

export type QuestionType =
  | "multiple_choice" | "multiple_select" | "true_false" | "numerical" | "matching"
  | "ordering" | "short_answer" | "essay" | "code" | "fill_blank"

export const TYPE_LABEL: Record<QuestionType, string> = {
  multiple_choice: "Opción múltiple",
  multiple_select: "Selección múltiple",
  true_false: "Verdadero / Falso",
  numerical: "Numérica",
  matching: "Emparejar",
  ordering: "Ordenar",
  short_answer: "Respuesta corta",
  essay: "Respuesta larga",
  code: "Código",
  fill_blank: "Completar espacios",
}

export const FREE_TYPES: QuestionType[] = ["short_answer", "essay", "code", "fill_blank"]
export const isFreeType = (t: string): boolean => (FREE_TYPES as string[]).includes(t)

export type TestQuestion = {
  index: number
  question_text: string
  question_type: QuestionType
  content: Record<string, unknown>
}

export type StartedRun = { id: string; section_quiz_id: string; status: string; source: "live" | "backup"; questions: TestQuestion[] }

export type JevCriterion = { text: string; p: number; matchedLocally?: boolean }
export type JevSlot =
  | { status: "not_applicable" | "skipped" }
  | { status: "unavailable"; reason: string }
  | {
      status: "ok"; verdict: boolean; criteria: JevCriterion[]; vetoP?: number; vetoed: boolean; modelVersion: string
      vetos?: { contradiction: number; falseClaim: number; keywordList: number }
    }

export type KeyCheck =
  | { status: "not_applicable" }
  | { status: "unavailable"; reason: string }
  | { status: "ok"; supported: boolean; usedLessonText: boolean; modelVersion: string; checks: Array<{ instruction: string; expect: "high" | "low"; gate: boolean; p: number }> }

export type TestResult = {
  index: number
  question_text: string
  question_type: QuestionType
  content: Record<string, unknown>
  answer: unknown
  correct_answer: unknown
  explanation: string | null
  current: { status: "ok" | "unavailable" | "skipped"; verdict: boolean | null }
  jev: JevSlot
  disagreement: boolean
  key_check: KeyCheck
}

export type KeyIssue = "none" | "wrong_key" | "ambiguous" | "bad_question" | "other"
export type Feedback = { question_index: number; answer_should_be: "correct" | "incorrect" | null; key_issue: KeyIssue | null; comment: string | null }

export type Summary = {
  questions: number; disagreements: number; labeled: number
  current: { agree: number; disagree: number }
  jev: { agree: number; disagree: number; unavailable: number }
  keyIssues: number
}

export const KEY_ISSUE_LABEL: Record<KeyIssue, string> = {
  none: "La clave es correcta",
  wrong_key: "La clave es incorrecta",
  ambiguous: "La pregunta es ambigua",
  bad_question: "La pregunta está mal planteada",
  other: "Otro problema",
}

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : [])

/** Respuesta o clave en texto legible, según el tipo (usa `content` para mostrar opciones, parejas y pasos). */
export function formatValue(type: QuestionType, value: unknown, content: Record<string, unknown>): string {
  if (value === undefined || value === null) return "(sin responder)"
  switch (type) {
    case "multiple_choice":
      return typeof value === "number" ? (strings(content.options)[value] ?? `opción ${value + 1}`) : String(value)
    case "multiple_select":
      return Array.isArray(value) && value.length
        ? (value as number[]).map((i) => strings(content.options)[i] ?? `opción ${i + 1}`).join("; ")
        : "(ninguna)"
    case "true_false":
      return value === true ? "Verdadero" : value === false ? "Falso" : String(value)
    case "numerical": {
      if (typeof value === "number") return String(value)
      const k = value as { value?: number; tolerance?: number }
      return k.value === undefined ? JSON.stringify(value) : `${k.value}${k.tolerance ? ` (± ${k.tolerance})` : ""}`
    }
    case "matching": {
      const left = strings(content.left)
      const right = strings(content.right)
      return Array.isArray(value) ? (value as number[]).map((r, i) => `${left[i] ?? i + 1} → ${right[r] ?? "?"}`).join("; ") : String(value)
    }
    case "ordering": {
      const items = strings(content.items)
      return Array.isArray(value) ? (value as number[]).map((i, n) => `${n + 1}. ${items[i] ?? "?"}`).join("  ") : String(value)
    }
    case "fill_blank":
      return Array.isArray(value) ? (value as string[]).map((v, i) => `${i + 1}: ${v || "(vacío)"}`).join("  ") : String(value)
    default:
      return Array.isArray(value) ? (value as string[]).map((v, i) => `${i + 1}. ${v}`).join("\n") : String(value)
  }
}

/** Una respuesta está completa cuando se puede enviar con la forma que valida la API. */
export function isAnswered(type: QuestionType, value: unknown, content: Record<string, unknown>): boolean {
  if (value === undefined || value === null) return false
  switch (type) {
    case "multiple_choice": return typeof value === "number"
    case "numerical": return typeof value === "number" && Number.isFinite(value)
    case "true_false": return typeof value === "boolean"
    case "multiple_select": return Array.isArray(value) && value.length > 0
    case "short_answer": case "essay": case "code": return typeof value === "string" && value.trim() !== ""
    case "fill_blank": return Array.isArray(value) && (value as string[]).some((v) => v.trim() !== "")
    case "matching": return Array.isArray(value) && (value as unknown[]).length === strings(content.left).length && (value as unknown[]).every((v) => typeof v === "number")
    case "ordering": {
      const n = strings(content.items).length
      return Array.isArray(value) && value.length === n && new Set(value as number[]).size === n && (value as unknown[]).every((v) => typeof v === "number")
    }
    default: return false
  }
}

/** Solo viajan las respuestas completas; las demás quedan sin responder (la API las cuenta como incorrectas). */
export function buildAnswers(questions: TestQuestion[], values: Record<number, unknown>) {
  return questions
    .filter((q) => isAnswered(q.question_type, values[q.index], q.content))
    .map((q) => ({ question_index: q.index, answer: values[q.index] }))
}

/** Parte "texto con ___" en los segmentos entre espacios. */
export function splitBlanks(textWithBlanks: unknown): string[] {
  return typeof textWithBlanks === "string" ? textWithBlanks.split("___") : [""]
}

export function verdictLabel(v: boolean | null): string {
  return v === null ? "—" : v ? "Correcta" : "Incorrecta"
}

/** Mayor P(sí) legible: "0.82" */
export const fmtP = (p: number): string => p.toFixed(2)

export function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
